import { Badge, Button, Card, Checkbox, IconTile, Spinner, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileArchive, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { AnalyzedSkillSchema, SkillImportResponse } from "@/api";
import { analyzeImportMutation, errorMessage, importSkillsMutation, listSkillsQueryKey } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";

type Step = "upload" | "select" | "done";

/** The import `selection` field: omitted (import everything) when all are selected, else the chosen paths. */
export function importSelection(selected: ReadonlySet<string>, total: number): string | null {
  return selected.size === total ? null : JSON.stringify({ skill_paths: [...selected] });
}

/**
 * /build/skills/import — the import flow as a page (not the old two-step
 * modal): drop a .zip → analyze → choose what to import → done.
 */
export function ImportSkillsPage() {
  const { t } = useTranslation("skills");
  const areas = useAreaCrumbs();
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzedSkillSchema[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SkillImportResponse | null>(null);

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.build, { label: t("title"), to: "/build/skills" }]}
        current={t("importPage.title")}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-8">
        <Text variant="body-sm" tone="secondary">
          {t("importPage.body")}
        </Text>
        {step === "upload" && (
          <UploadStep
            onAnalyzed={(f, skills) => {
              setFile(f);
              setAnalysis(skills);
              setSelected(new Set(skills.map((s) => s.sourcePath)));
              setStep("select");
            }}
          />
        )}
        {step === "select" && file && (
          <SelectStep
            file={file}
            analysis={analysis}
            selected={selected}
            onToggle={(path) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(path)) next.delete(path);
                else next.add(path);
                return next;
              })
            }
            onCancel={() => setStep("upload")}
            onDone={(res) => {
              setResult(res);
              setStep("done");
            }}
          />
        )}
        {step === "done" && result && (
          <DoneStep
            result={result}
            onMore={() => {
              setFile(null);
              setAnalysis([]);
              setResult(null);
              setStep("upload");
            }}
          />
        )}
      </div>
    </div>
  );
}

function UploadStep({ onAnalyzed }: Readonly<{ onAnalyzed: (file: File, skills: AnalyzedSkillSchema[]) => void }>) {
  const { t } = useTranslation("skills");
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const analyze = useMutation(analyzeImportMutation());

  const pick = (f: File | null | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error(t("importPage.wrongType"));
      return;
    }
    analyze.mutate(
      { body: { file: f } },
      {
        onSuccess: (res) => onAnalyzed(f, res.data.skills ?? []),
        onError: (error) => toast.error(errorMessage(error, t("importPage.analyzeFailed"))),
      },
    );
  };

  return (
    <button
      type="button"
      aria-label={t("importPage.dropzone")}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        pick(e.dataTransfer.files?.[0]);
      }}
      className={`flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 border-dashed px-6 py-16 text-center transition-colors ${
        dragging ? "border-brand-primary bg-brand-primary-subtle" : "border-border-input"
      }`}
    >
      {analyze.isPending ? (
        <>
          <Spinner size="lg" />
          <Text variant="body" tone="secondary">
            {t("importPage.analyzing")}
          </Text>
        </>
      ) : (
        <>
          <IconTile variant="brand">
            <Upload size={18} aria-hidden />
          </IconTile>
          <Text variant="body">{t("importPage.dropzone")}</Text>
          <Text variant="body-sm" tone="tertiary">
            {t("importPage.choose")}
          </Text>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => pick(e.target.files?.[0])}
      />
    </button>
  );
}

function SelectStep({
  file,
  analysis,
  selected,
  onToggle,
  onCancel,
  onDone,
}: Readonly<{
  file: File;
  analysis: AnalyzedSkillSchema[];
  selected: Set<string>;
  onToggle: (path: string) => void;
  onCancel: () => void;
  onDone: (result: SkillImportResponse) => void;
}>) {
  const { t } = useTranslation(["skills", "build"]);
  const toast = useToast();
  const queryClient = useQueryClient();

  const doImport = useMutation({
    ...importSkillsMutation(),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: listSkillsQueryKey() });
      onDone(res.data);
    },
    onError: (error) => toast.error(errorMessage(error, t("importPage.importFailed"))),
  });

  const submit = () => {
    doImport.mutate({ body: { file, selection: importSelection(selected, analysis.length) } });
  };

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Text variant="h3">{t("importPage.selectTitle")}</Text>
        <ul className="flex flex-col divide-y divide-border-subtle">
          {analysis.map((skill) => (
            <li key={skill.sourcePath} className="flex items-start gap-3 py-3">
              <Checkbox
                aria-label={skill.name}
                checked={selected.has(skill.sourcePath)}
                onChange={() => onToggle(skill.sourcePath)}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Text variant="body">{skill.name}</Text>
                  {skill.nameTaken && <Badge variant="warning">{t("importPage.nameTaken")}</Badge>}
                  {(skill.agents?.length ?? 0) > 0 && (
                    <Badge variant="info">{t("importPage.agentsDetected", { count: skill.agents?.length ?? 0 })}</Badge>
                  )}
                </div>
                {skill.description && (
                  <Text variant="body-sm" tone="secondary">
                    {skill.description}
                  </Text>
                )}
                {(skill.missingTools?.length ?? 0) > 0 && (
                  <Text variant="caption" tone="warning">
                    {t("importPage.missingTools", { tools: skill.missingTools?.join(", ") })}
                  </Text>
                )}
                {(skill.disabledTools?.length ?? 0) > 0 && (
                  <Text variant="caption" tone="warning">
                    {t("importPage.disabledTools", { tools: skill.disabledTools?.join(", ") })}
                  </Text>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t("build:delete.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={doImport.isPending} disabled={selected.size === 0}>
            {t("importPage.importAction")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function DoneStep({ result, onMore }: Readonly<{ result: SkillImportResponse; onMore: () => void }>) {
  const { t } = useTranslation("skills");
  const navigate = useNavigate();
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <IconTile variant="success">
          <FileArchive size={18} aria-hidden />
        </IconTile>
        <Text variant="h3">{t("importPage.doneTitle")}</Text>
        <Text variant="body-sm" tone="secondary">
          {t("importPage.doneBody", { count: result.createdCount })}
        </Text>
        {(result.createdAgentCount ?? 0) > 0 && (
          <Text variant="body-sm" tone="secondary">
            {t("importPage.doneAgents", { count: result.createdAgentCount })}
          </Text>
        )}
        <div className="mt-2 flex gap-2">
          <Button variant="secondary" onClick={onMore}>
            {t("importPage.importMore")}
          </Button>
          <Button variant="primary" onClick={() => navigate("/build/skills")}>
            {t("importPage.viewSkills")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
